import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BiHelpCircle } from 'react-icons/bi'
import Footer from '../../components/Footer'
import HomeHeader from '../../components/HomeHeader'
import Sidebar from '../../components/SideBar'
import ConfigurationContent from './components/configuration'
import PlanViewer from './components/PlanViewer'
import PlanSelector from './components/PlanSelector'
import PlanActions from './components/PlanActions'
import PlanningEmptyState from './components/PlanningEmptyState'
import { useStudyPlanManager } from './hooks/useStudyPlanManager'
import { planningApi } from './services/api'
import { RootState } from 'store/modules/rootReducer'
import { useTimerGuard } from '../../hooks/useTimerGuard'
import BlockTimerModal from '../../components/Timer/BlockTimerModal'
import { usePlanningTour } from './hooks/usePlanningTour'
import './styles.css'

const Planning: React.FC = () => {
  const { currentPlan, plans, loading, mode, selectPlan, deletePlan } = useStudyPlanManager()
  const { isDarkMode } = useSelector((state: RootState) => state.theme)
  const history = useHistory()
  const { checkAndBlock } = useTimerGuard()
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [currentStep, setCurrentStep] = useState(1)

  // Use planning tour hook
  const { wizardTour, dashboardTour } = usePlanningTour({
    currentStep,
    isDarkMode,
    mode,
    hasPlans: plans.length > 0,
  })

  const handleRestartCycle = async () => {
    if (!currentPlan?._id) return

    const action = async () => {
      const confirmed = window.confirm(
        'Tem certeza que deseja reiniciar o ciclo? Todo o progresso será perdido.'
      )
      if (!confirmed) return

      try {
        await planningApi.restartCycle(currentPlan._id)
        toast.success('Ciclo reiniciado com sucesso')
        window.location.reload()
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Erro ao reiniciar ciclo')
      }
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    await action()
  }

  const handleEditPlan = () => {
    if (!currentPlan?._id) return

    const action = () => {
      history.push(`/planejamento/${currentPlan._id}`)
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    action()
  }

  const handleCreatePlan = () => {
    history.push('/planejamento/novo')
  }

  const handleDeletePlan = async () => {
    if (!currentPlan?._id) return

    const action = async () => {
      const confirmed = window.confirm(
        'Tem certeza que deseja deletar este plano de estudos? Esta ação não pode ser desfeita.'
      )
      if (!confirmed) return

      try {
        await deletePlan(currentPlan._id)
      } catch (error) {
        console.error('Error deleting plan:', error)
        toast.error('Erro ao deletar plano de estudos. Tente novamente.')
      }
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    await action()
  }

  const handleCloseBlockModal = () => {
    setIsBlockModalOpen(false)
    setPendingAction(null)
  }

  const handleFinishSession = () => {
    setIsBlockModalOpen(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }
  // Determina qual componente renderizar baseado no modo
  const renderContent = () => {
    // Empty state: quando não há planos criados
    if (mode === 'view' && plans.length === 0) {
      return <PlanningEmptyState isDarkMode={isDarkMode} />
    }

    // Modo visualização: mostra o plano atual
    if (mode === 'view' && plans.length > 0) {
      return (
        <>
          <div className="planning-header">
            <div className="planning-header-content">
              <div className="planning-header-title-wrapper">
                <h1 className="configuration-header-content-title">Planejador de estudos</h1>
                <button
                  className="planning-help-button"
                  onClick={() => dashboardTour.startTour(true)}
                  title="Iniciar tour guiado"
                  aria-label="Iniciar tour guiado do dashboard"
                >
                  <BiHelpCircle size={24} />
                </button>
              </div>
              <div className="planning-header-controls">
                <PlanSelector
                  currentPlan={currentPlan}
                  plans={plans}
                  onSelectPlan={selectPlan}
                  onDeletePlan={deletePlan}
                  isDarkMode={isDarkMode}
                />
                <PlanActions
                  onCreate={handleCreatePlan}
                  onRestart={handleRestartCycle}
                  onEdit={handleEditPlan}
                  onDelete={handleDeletePlan}
                  disabled={!currentPlan}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          </div>
          <PlanViewer plan={currentPlan} loading={loading} isDarkMode={isDarkMode} />
        </>
      )
    }

    // Modo criação ou edição: mostra o formulário de configuração
    return (
      <ConfigurationContent
        existingPlan={mode === 'edit' ? currentPlan : null}
        mode={mode}
        onDelete={mode === 'edit' ? deletePlan : undefined}
        onStepChange={setCurrentStep}
        wizardTour={wizardTour}
      />
    )
  }

  return (
    <>
      <Sidebar />
      <HomeHeader />
      <section className={`planning-container ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="planning-content">{renderContent()}</div>
      </section>
      <Footer />
      <BlockTimerModal
        isOpen={isBlockModalOpen}
        onClose={handleCloseBlockModal}
        onFinish={handleFinishSession}
      />
    </>
  )
}

export default Planning
